import { useSearchParams } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Share2,
  SlidersHorizontal,
  User,
  Users,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import {
  AccountSettings,
  SocialMediaSettings,
  AdditionalInfoSettings,
  DocumentsSettings,
  BrandsSettings,
  DangerZoneSettings,
} from '@/components/settings'

const SECTIONS = [
  { id: 'account', label: 'Account', blurb: 'Name, email, password, visibility', icon: User },
  { id: 'documents', label: 'Documents', blurb: 'Contracts and files', icon: FileText },
  { id: 'social', label: 'Social media', blurb: 'Connected accounts', icon: Share2 },
  { id: 'managers', label: 'Managers', blurb: 'Who represents you', icon: Users },
  {
    id: 'additional-info',
    label: 'Additional info',
    blurb: 'School, sports, rates, preferences',
    icon: SlidersHorizontal,
  },
] as const
type SectionId = (typeof SECTIONS)[number]['id']

/**
 * Settings. On a phone it is a list of sections, each opening on its own screen with a back
 * link, so nothing scrolls sideways. On a wider screen the sections sit in a wrapping row of
 * pills above the content. `?tab=` deep links work for both.
 */
const Settings = () => {
  const {
    athlete,
    isLoading,
    profileLoading,
    disconnectSocialMedia,
    updatePreferences,
    updateSports,
    updateSchool,
    updateProfile,
    updateAvatar,
  } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()

  const tabFromUrl = searchParams.get('tab')
  const valid = SECTIONS.find((s) => s.id === tabFromUrl)?.id ?? null
  // Desktop always shows a section (Account by default); a phone shows the list until one is picked
  const active: SectionId | null = valid ?? (isMobile ? null : 'account')
  const isWelcome = searchParams.get('welcome') === 'true'

  const open = (id: SectionId) => setSearchParams({ tab: id })
  const backToList = () => setSearchParams({})

  if (isLoading && !athlete) {
    return (
      <div className="w-full pb-4">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  const current = SECTIONS.find((s) => s.id === active)

  const content = (
    <>
      {active === 'account' && (
        <div className="space-y-6">
          <AccountSettings
            athlete={athlete}
            profileLoading={profileLoading}
            updateProfile={updateProfile}
            updateAvatar={updateAvatar}
          />
          <DangerZoneSettings />
        </div>
      )}
      {active === 'documents' && <DocumentsSettings athlete={athlete} />}
      {active === 'social' && (
        <SocialMediaSettings
          athlete={athlete}
          isWelcome={isWelcome}
          disconnectSocialMedia={disconnectSocialMedia}
        />
      )}
      {active === 'managers' && <BrandsSettings athlete={athlete} />}
      {active === 'additional-info' && (
        <AdditionalInfoSettings
          athlete={athlete}
          updatePreferences={updatePreferences}
          updateSports={updateSports}
          updateSchool={updateSchool}
        />
      )}
    </>
  )

  // Phone: a list of sections, or one section with a back link
  if (isMobile) {
    if (!active) {
      return (
        <div className="w-full pb-4">
          <h1 className="mb-1 text-2xl font-bold text-foreground">Settings</h1>
          <p className="mb-5 text-muted-foreground">Manage your account and preferences</p>
          <ul className="space-y-2">
            {SECTIONS.map(({ id, label, blurb, icon: Icon }) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => open(id)}
                  className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition hover:bg-muted"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-foreground">{label}</span>
                    <span className="block text-xs text-muted-foreground">{blurb}</span>
                  </span>
                  <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )
    }
    return (
      <div className="w-full pb-4">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Settings
        </button>
        <h1 className="mb-4 text-2xl font-bold text-foreground">{current?.label}</h1>
        {content}
      </div>
    )
  }

  // Wider screens: wrapping pills above the section
  return (
    <div className="w-full pb-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Manage your account and preferences</p>
      </div>
      <nav className="mb-6 flex flex-wrap gap-2" aria-label="Settings sections">
        {SECTIONS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => open(id)}
            aria-current={active === id ? 'page' : undefined}
            className={cn(
              'rounded-full border px-4 py-2 text-sm font-medium transition',
              active === id
                ? 'border-[#C0FD72] bg-[#C0FD72] text-black'
                : 'border-border bg-card text-foreground hover:bg-muted',
            )}
          >
            {label}
          </button>
        ))}
      </nav>
      {content}
    </div>
  )
}

export default Settings
